package at.theatervogelweide.admin.web.screens

import org.jetbrains.compose.web.dom.Ul
import org.jetbrains.compose.web.dom.Li
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.AdminResult
import at.theatervogelweide.admin.contract.overview.ImageCounts
import at.theatervogelweide.admin.contract.overview.ActCounts
import at.theatervogelweide.admin.contract.overview.NewsCounts
import at.theatervogelweide.admin.contract.overview.Overview
import at.theatervogelweide.admin.contract.overview.OverviewService
import at.theatervogelweide.admin.contract.overview.PeopleCounts
import at.theatervogelweide.admin.contract.overview.UpcomingAct
import at.theatervogelweide.admin.web.Route
import at.theatervogelweide.admin.web.components.AdminBadge
import at.theatervogelweide.admin.web.components.AdminCard
import at.theatervogelweide.admin.web.components.AdminPageBody
import at.theatervogelweide.admin.web.components.AdminPageHeader
import at.theatervogelweide.admin.web.components.BadgeVariant
import at.theatervogelweide.admin.web.components.Bem
import at.theatervogelweide.admin.web.components.PublicSiteLink
import at.theatervogelweide.admin.web.components.RouteLink
import at.theatervogelweide.admin.web.rpcCall
import at.theatervogelweide.admin.web.screens.acts.TicketScheduleSteps
import at.theatervogelweide.admin.web.screens.acts.now
import at.theatervogelweide.domain.NewsVisibility
import kotlinx.coroutines.delay
import kotlinx.datetime.LocalDateTime
import kotlinx.rpc.RpcClient
import kotlinx.rpc.withService
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlin.time.Duration.Companion.minutes

private val button = Bem("admin-button")
private val statTiles = Bem("admin-stat-tiles")
private val statTile = Bem("admin-stat-tile")
private val overviewBlock = Bem("admin-overview")
private val alert = Bem("admin-alert")

/** "2 öffentlich · 1 geplant", leaving out what there is none of. */
internal fun NewsCounts.metaText(): String =
    NewsVisibility.entries.filter { count(it) > 0 }.joinToString(" · ") { "${count(it)} ${it.label.lowercase()}" }
        .ifEmpty { "Noch keine" }

/** "1987 – 2026", one year alone, or nothing without acts. */
internal fun ActCounts.metaText(): String = when {
    firstYear == null || lastYear == null -> "Noch keine"
    firstYear == lastYear -> "$firstYear"
    else -> "$firstYear – $lastYear"
}

internal fun PeopleCounts.metaText(): String = if (withoutPhoto == 0) "Alle mit Foto" else "$withoutPhoto ohne Foto"

/** "3 Bilder ohne Beschreibung", and where once they are in more than one place: "(2 in Stücken, 1 bei Personen)". */
internal fun ImageCounts.checkText(): String {
    val total = if (withoutDescription == 1) "1 Bild ohne Beschreibung" else "$withoutDescription Bilder ohne Beschreibung"
    val places = listOf(
        actsWithoutDescription to "in Stücken",
        newsWithoutDescription to "in Neuigkeiten",
        peopleWithoutDescription to "bei Personen",
    ).filter { it.first > 0 }
    return if (places.size > 1) "$total (${places.joinToString(", ") { "${it.first} ${it.second}" }})" else total
}

/** "WWW.WALTHER.COM · 6 Vorstellungen · Reservierungs-Link hinterlegt" */
internal fun UpcomingAct.summary(): String = listOf(
    act.title,
    if (act.performances.size == 1) "1 Vorstellung" else "${act.performances.size} Vorstellungen",
    if (hasReservationUrl) "Reservierungs-Link hinterlegt" else "ohne Reservierungs-Link",
).joinToString(" · ")

/** "1 Rolle in „Walther“ ist noch nicht besetzt: Souffleuse" */
internal fun UpcomingAct.unstaffedText(): String {
    val count = rolesWithoutPeople.size
    val roles = if (count == 1) "1 Rolle in „${act.title}“ ist" else "$count Rollen in „${act.title}“ sind"
    return "$roles noch nicht besetzt: ${rolesWithoutPeople.joinToString(", ")}"
}

/** Whether the ticket page shows the act at [now] already, or only announces it later. */
private fun UpcomingAct.badge(now: LocalDateTime): Pair<String, BadgeVariant> =
    if (now >= ticketSchedule.announcedFrom) "Öffentlich" to BadgeVariant.PUBLIC else "Geplant" to BadgeVariant.PLANNED

/**
 * Übersicht (#24): the ticket schedule of the upcoming act, counts of what the other screens edit, and links to
 * create something new. [onNavigate] opens another screen.
 */
@Composable
fun OverviewScreen(rpc: RpcClient, onNavigate: (Route) -> Unit) {
    val service = remember(rpc) { rpc.withService<OverviewService>() }
    var overview by remember { mutableStateOf<Overview?>(null) }
    var error by remember { mutableStateOf<String?>(null) }

    var now by remember { mutableStateOf(now()) }

    // reloads every minute while the screen stays open: a performance that starts can make another act the upcoming
    // one, and news become public or expire; a failed reload keeps what is shown
    LaunchedEffect(service) {
        while (true) {
            when (val result = rpcCall { service.overview() }) {
                is AdminResult.Success -> {
                    overview = result.value
                    error = null
                }

                is AdminResult.Failure -> if (overview == null) error = result.error.message
            }
            now = now()
            delay(1.minutes)
        }
    }

    AdminPageHeader("Übersicht") {
        RouteLink(Route.News(new = true), onNavigate, button.block) { Text("+ Neuigkeit") }
        RouteLink(Route.Acts(new = true), onNavigate, button.block) { Text("+ Stück") }
        RouteLink(Route.People(new = true), onNavigate, button.block) { Text("+ Person") }
    }

    AdminPageBody(overviewBlock.block) {
        error?.let {
            P({
                classes(alert.block)
                attr("role", "alert")
            }) { Text(it) }
        }

        val loaded = overview ?: run {
            if (error == null) {
                P({ classes(overviewBlock.element("message").className) }) { Text("Lade …") }
            }
            return@AdminPageBody
        }

        TicketCard(loaded.upcomingAct, now, onNavigate)

        Div({ classes(statTiles.block) }) {
            StatTile("Neuigkeiten", loaded.news.total, loaded.news.metaText(), Route.News(), onNavigate)
            StatTile("Stücke im Archiv", loaded.acts.total, loaded.acts.metaText(), Route.Acts(), onNavigate)
            StatTile("Personen", loaded.people.total, loaded.people.metaText(), Route.People(), onNavigate)

            ChecksTile(loaded, onNavigate)
        }
    }
}

/**
 * "Zu prüfen": the upcoming act's roles nobody plays yet and the images without a description (#105), left out when
 * there is neither. With one check the tile is a link where one place fixes it (the roles: that act); with both, a
 * list whose roles line is the link. The images are on several screens, so they link nowhere.
 */
@Composable
private fun ChecksTile(overview: Overview, onNavigate: (Route) -> Unit) {
    val unstaffed = overview.upcomingAct?.takeIf { it.rolesWithoutPeople.isNotEmpty() }
    val images = overview.images.takeIf { it.withoutDescription > 0 }
    when {
        unstaffed != null && images == null ->
            RouteLink(Route.Acts(unstaffed.act.id), onNavigate, statTile.block, statTile.modifier("attention")) {
                Span({ classes(statTile.element("label").className) }) { Text("Zu prüfen") }
                Span({ classes(overviewBlock.element("check").className) }) { Text(unstaffed.unstaffedText()) }
            }

        unstaffed == null && images != null -> Div({ classes(statTile.block, statTile.modifier("attention")) }) {
            Span({ classes(statTile.element("label").className) }) { Text("Zu prüfen") }
            Span({ classes(overviewBlock.element("check").className) }) { Text(images.checkText()) }
        }

        unstaffed != null && images != null -> Div({ classes(statTile.block, statTile.modifier("attention")) }) {
            Span({ classes(statTile.element("label").className) }) { Text("Zu prüfen") }
            Ul({ classes(statTile.element("items").className) }) {
                Li { RouteLink(Route.Acts(unstaffed.act.id), onNavigate) { Text(unstaffed.unstaffedText()) } }
                Li { Text(images.checkText()) }
            }
        }
    }
}

@Composable
private fun TicketCard(upcoming: UpcomingAct?, now: LocalDateTime, onNavigate: (Route) -> Unit) {
    AdminCard(
        title = "Kartenreservierung",
        id = "kartenreservierung",
        header = upcoming?.let {
            {
                Div({ classes(overviewBlock.element("actions").className) }) {
                    PublicSiteLink("/tickets", button.block) { Text("Seite ansehen ↗") }
                    RouteLink(Route.Acts(it.act.id), onNavigate, button.block, button.modifier("primary")) { Text("Termine bearbeiten") }
                }
            }
        },
    ) {
        if (upcoming == null) {
            P({ classes(overviewBlock.element("empty").className) }) {
                Text("Derzeit ist kein Stück mit Kartenverkauf geplant. Sobald ein Stück Vorstellungen und „Kartenverkauf ab“ hat, steht hier sein Zeitplan.")
            }
            return@AdminCard
        }

        P({ classes(overviewBlock.element("summary").className) }) {
            val (text, variant) = upcoming.badge(now)
            AdminBadge(text, variant)
            Span { Text(upcoming.summary()) }
        }
        TicketScheduleSteps(upcoming.ticketSchedule, now)
    }
}

@Composable
private fun StatTile(label: String, value: Int, meta: String, route: Route, onNavigate: (Route) -> Unit) {
    RouteLink(route, onNavigate, statTile.block) {
        Span({ classes(statTile.element("label").className) }) { Text(label) }
        Span({ classes(statTile.element("value").className) }) { Text(value.toString()) }
        Span({ classes(statTile.element("meta").className) }) { Text(meta) }
    }
}
